import { GameObjects, Scene } from 'phaser';
import type { LearningReviewContent, LearningReviewItem } from '../domain/learningReview';

export const RESULT_OVERLAY_DEPTH = 10_000;
const PANEL_WIDTH = 920;
const PANEL_HEIGHT = 640;
const CARD_WIDTH = 830;
const CARD_HEIGHT = 300;

export type LearningResultOverlayOptions = {
  centerX: number;
  centerY: number;
  viewportWidth: number;
  viewportHeight: number;
  scale?: number;
  title: string;
  titleColor: string;
  accentColor: number;
  summaryLines: string[];
  reviewItems: LearningReviewItem[];
  emptyReviewMessage?: string;
  restartEnabled: boolean;
  restartLabel: string;
  onRestart: () => void;
  resolveTextureKey: (mediaUrl: string) => string | undefined;
};

function addFittedImage(
  scene: Scene,
  host: GameObjects.Container,
  x: number,
  y: number,
  mediaUrl: string | undefined,
  maxWidth: number,
  maxHeight: number,
  resolveTextureKey: LearningResultOverlayOptions['resolveTextureKey'],
): GameObjects.Image | undefined {
  if (!mediaUrl) return undefined;
  const key = resolveTextureKey(mediaUrl);
  if (!key || !scene.textures.exists(key)) return undefined;
  const image = scene.add.image(x, y, key).setOrigin(0.5);
  image.setScale(Math.min(maxWidth / Math.max(1, image.width), maxHeight / Math.max(1, image.height)));
  host.add(image);
  return image;
}

function contentText(content: LearningReviewContent): string {
  if (content.text.trim()) return content.text.trim();
  return content.mediaUrl ? 'Görsel seçenek' : '—';
}

function addButton(
  scene: Scene,
  host: GameObjects.Container,
  x: number,
  y: number,
  width: number,
  label: string,
  onPress: () => void,
  enabled = true,
): GameObjects.Container {
  const button = scene.add.container(x, y).setSize(width, 54);
  const background = scene.add.rectangle(0, 0, width, 54, enabled ? 0xf4ca5d : 0x587078, enabled ? 1 : 0.55)
    .setStrokeStyle(2, enabled ? 0xffefb0 : 0x789098);
  const text = scene.add.text(0, 0, label, {
    fontFamily: 'system-ui, sans-serif', fontSize: '19px', fontStyle: 'bold', color: enabled ? '#12333a' : '#d3dfe1',
  }).setOrigin(0.5);
  button.add([background, text]);
  if (enabled) button.setInteractive({ useHandCursor: true }).on('pointerdown', onPress);
  host.add(button);
  return button;
}

function addAnswerRow(
  scene: Scene,
  host: GameObjects.Container,
  y: number,
  heading: string,
  contents: LearningReviewContent[],
  color: string,
  resolveTextureKey: LearningResultOverlayOptions['resolveTextureKey'],
) {
  const headingText = scene.add.text(-385, y, heading, {
    fontFamily: 'system-ui, sans-serif', fontSize: '18px', fontStyle: 'bold', color,
  }).setOrigin(0, 0.5);
  host.add(headingText);
  const shown = contents.slice(0, 3);
  const labels = shown.map(contentText).join('  •  ') || 'Seçim yapılmadı';
  const label = scene.add.text(-205, y, labels, {
    fontFamily: 'system-ui, sans-serif', fontSize: '18px', fontStyle: 'bold', color: '#f1fbfc',
    wordWrap: { width: 540, useAdvancedWrap: true },
  }).setOrigin(0, 0.5).setMaxLines(2);
  host.add(label);
  shown.filter((content) => content.mediaUrl).slice(0, 2).forEach((content, index) => {
    addFittedImage(scene, host, 305 + index * 55, y, content.mediaUrl, 46, 46, resolveTextureKey);
  });
}

export function createLearningResultOverlay(
  scene: Scene,
  options: LearningResultOverlayOptions,
): GameObjects.Container {
  const root = scene.add.container(options.centerX, options.centerY)
    .setScale(options.scale ?? 1)
    .setDepth(RESULT_OVERLAY_DEPTH);
  const veil = scene.add.rectangle(0, 0, options.viewportWidth, options.viewportHeight, 0x06191f, 0.82);
  const panel = scene.add.rectangle(0, 0, PANEL_WIDTH, PANEL_HEIGHT, 0x0c2c35, 0.985)
    .setStrokeStyle(5, options.accentColor);
  const title = scene.add.text(0, -274, options.title, {
    fontFamily: 'system-ui, sans-serif', fontSize: '38px', fontStyle: 'bold', color: options.titleColor, align: 'center',
    wordWrap: { width: 820, useAdvancedWrap: true },
  }).setOrigin(0.5).setMaxLines(2);
  const summary = scene.add.text(0, -213, options.summaryLines.join('\n'), {
    fontFamily: 'system-ui, sans-serif', fontSize: '19px', color: '#d7e9e9', align: 'center', lineSpacing: 5,
    wordWrap: { width: 820, useAdvancedWrap: true },
  }).setOrigin(0.5).setMaxLines(3);
  const reviewTitle = scene.add.text(-405, -154, options.reviewItems.length > 0
    ? `YANLIŞLARDAN ÖĞREN · ${options.reviewItems.length} SORU`
    : 'ÖĞRENME ÖZETİ', {
      fontFamily: 'system-ui, sans-serif', fontSize: '18px', fontStyle: 'bold', color: '#ffd76a',
    }).setOrigin(0, 0.5);
  const card = scene.add.rectangle(0, 4, CARD_WIDTH, CARD_HEIGHT, 0x123942, 0.96).setStrokeStyle(2, 0x527a7f);
  const pageHost = scene.add.container(0, 0);
  root.add([veil, panel, title, summary, reviewTitle, card, pageHost]);

  let pageIndex = 0;
  const drawPage = () => {
    pageHost.removeAll(true);
    if (options.reviewItems.length === 0) {
      const success = scene.add.text(0, 0, options.emptyReviewMessage ?? 'Tüm soruları doğru cevapladın. Harika iş!', {
        fontFamily: 'system-ui, sans-serif', fontSize: '27px', fontStyle: 'bold', color: '#9ef0bd', align: 'center',
        wordWrap: { width: 740, useAdvancedWrap: true },
      }).setOrigin(0.5).setMaxLines(3);
      pageHost.add(success);
      return;
    }
    const item = options.reviewItems[pageIndex];
    const pageLabel = scene.add.text(385, -135, `${pageIndex + 1} / ${options.reviewItems.length}`, {
      fontFamily: 'system-ui, sans-serif', fontSize: '17px', fontStyle: 'bold', color: '#b9d3d4',
    }).setOrigin(1, 0.5);
    const itemLabel = scene.add.text(-385, -130, item.label, {
      fontFamily: 'system-ui, sans-serif', fontSize: '21px', fontStyle: 'bold', color: '#ffe08a',
    }).setOrigin(0, 0.5);
    pageHost.add([pageLabel, itemLabel]);

    const promptImage = addFittedImage(scene, pageHost, -315, -67, item.prompt.mediaUrl, 115, 88, options.resolveTextureKey);
    const promptX = promptImage ? -235 : -385;
    const promptWidth = promptImage ? 600 : 770;
    const prompt = scene.add.text(promptX, -67, item.prompt.text.trim() || (item.prompt.mediaUrl ? 'Görsel soru' : 'Soru'), {
      fontFamily: 'system-ui, sans-serif', fontSize: '23px', fontStyle: 'bold', color: '#ffffff',
      wordWrap: { width: promptWidth, useAdvancedWrap: true }, lineSpacing: -2,
    }).setOrigin(0, 0.5).setMaxLines(4);
    pageHost.add(prompt);
    addAnswerRow(scene, pageHost, 28, 'Yanlış cevabın:', item.wrongAnswers, '#ff9c92', options.resolveTextureKey);
    addAnswerRow(scene, pageHost, 91, 'Doğru cevap:', [item.correctAnswer], '#8ff0b2', options.resolveTextureKey);
    const notes = scene.add.text(-385, 137, item.notes.join(' • '), {
      fontFamily: 'system-ui, sans-serif', fontSize: '15px', color: '#c2d9da',
      wordWrap: { width: 770, useAdvancedWrap: true },
    }).setOrigin(0, 0.5).setMaxLines(2);
    pageHost.add(notes);
  };
  drawPage();

  if (options.reviewItems.length > 1) {
    addButton(scene, root, -225, 224, 170, '← Önceki', () => { pageIndex = (pageIndex - 1 + options.reviewItems.length) % options.reviewItems.length; drawPage(); });
    addButton(scene, root, 225, 224, 170, 'Sonraki →', () => { pageIndex = (pageIndex + 1) % options.reviewItems.length; drawPage(); });
  }
  addButton(scene, root, 0, 282, 280, options.restartEnabled ? options.restartLabel : 'Sonuç kaydediliyor…', options.onRestart, options.restartEnabled);
  return root;
}
